import React, { useCallback, useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import { FaArrowRight } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { format, addYears } from 'date-fns'
import { useHistory } from 'react-router-dom'

import Yup from '~/services/yup'
import { api } from '~/services/api'

import masks from '~/util/masks'
import formUtils from '~/util/form'

import {
    Textbox, Button, Select, File
} from '~/components/Form'
import ActionFooter from '~/components/ActionFooter'

import { Container } from './styles'

const idadeMinima = 15

const validation = Yup.object({
    nome: Yup.string()
        .min(3, 'O nome deve ter pelo menos 3 caracteres.')
        .matches(/^[^@]*$/, 'Não pode conter o caractere "@"')
        .required('O nome é obrigatório'),
    data_nascimento: Yup.string()
        .date({ max: { value: addYears(new Date(), -idadeMinima), message: `Você deve ter pelo menos ${idadeMinima} anos para se associar.` } })
        .required('Especifique a data de nascimento.'),
    sexo: Yup.string().ensure().required('Especifique o sexo.'),
    email: Yup.string().email('E-mail inválido.').required('Especifique um e-mail.'),
    cpf: Yup.string().cpf('CPF inválido').required('Especifique o CPF.'),
    'cpf-arquivo': Yup.number().nullable().required('Anexe seu CPF.'),
    rg: Yup.string().required('Especifique o RG.'),
    rg_orgao: Yup.string().required('Especifique o órgão expedidos do RG.'),
    rg_data_emissao: Yup.string()
        .date({
            min: { value: Yup.ref('data_nascimento'), message: 'A data deve ser maior que a data de nascimento.' },
            max: { value: new Date(), message: 'A data não pode estar no futuro.' }
        })
        .required('Especifique a data de emissão do RG.'),
    'rg-arquivo': Yup.number().nullable().required('Anexe seu RG.'),
    foto: Yup.number().nullable().required('Envie sua foto 3X4.')
})

export default function ({ callback, loadStoredData }) {
    const history = useHistory()

    const [initialValues, setInitialValues] = useState({
        nome: '',
        data_nascimento: '',
        sexo: '',
        email: '',
        cpf: '',
        'cpf-arquivo': null,
        rg: '',
        rg_orgao: '',
        rg_data_emissao: '',
        'rg-arquivo': null,
        foto: null
    })

    useEffect(() => {
        const storageData = JSON.parse(localStorage.getItem('sindmepa-associacao-temp-data'))

        if (storageData?.pessoal) {
            const { data_nascimento, rg_data_emissao, sexo } = storageData.pessoal

            setInitialValues({
                ...storageData.pessoal,
                data_nascimento: format(new Date(`${data_nascimento}`), 'dd/MM/yyyy'),
                rg_data_emissao: format(new Date(`${rg_data_emissao}`), 'dd/MM/yyyy'),
                sexo: {
                    label: sexo === 'M' ? 'Masculino' : 'Feminino',
                    value: sexo
                }
            })
        }
    }, [])

    const handleSubmit = useCallback(async (values, { setSubmitting }) => {
        const data = formUtils.extractFormValues(values)

        const storageData = JSON.parse(localStorage.getItem('sindmepa-associacao-temp-data'))

        localStorage.setItem('sindmepa-associacao-temp-data', JSON.stringify({
            ...storageData,
            academico: true,
            pessoal: data
        }))

        setSubmitting(false)

        toast.success('Dados salvos! Por favor, continue o preenchimento.')

        callback()
    }, [callback])

    function setStoredData(data) {
        const { data_nascimento, rg_data_emissao, sexo } = data

        setInitialValues({
            ...data,
            data_nascimento: format(new Date(`${data_nascimento}`), 'dd/MM/yyyy'),
            rg_data_emissao: format(new Date(`${rg_data_emissao}`), 'dd/MM/yyyy'),
            sexo: { label: sexo === 'M' ? 'Masculino' : 'Feminino', value: sexo }
        })
    }

    const handleCPFBlur = useCallback(async (value, { resetForm }) => {
        if (!value || value.length < 14) {
            return
        }

        // Verificação de associação
        const associados = await api.get(`associado?cpf=${value}`)
        const associado = associados?.[0]

        switch (associado?.status_associacao.id) {
            case 2:
                toast.info('Você já solicitou sua associação. Basta aguardar o contato do SINDMEPA.', {
                    autoClose: 8000
                })

                resetForm()

                return
            case 3:
                toast.info('Você já está associado ao SINDMEPA. Faça login no Espaço Médico e acesse suas informações.', {
                    autoClose: 6000
                })

                setTimeout(() => { history.push('/') }, 2000)

                return
            default: break
        }

        loadStoredData(value, setStoredData)
    }, [loadStoredData, history])

    return (
        <Container>
            <Formik
                initialValues={initialValues}
                validationSchema={validation}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({
                    isSubmitting, setFieldValue, errors, resetForm, values
                }) => (
                    <Form>
                        <Textbox
                            name="cpf"
                            label="CPF"
                            mask={masks.cpf}
                            inputMode="numeric"
                            onBlur={e => {
                                handleCPFBlur(e.target.value, { resetForm })
                            }}
                        />

                        <Textbox
                            label="Nome completo"
                            name="nome"
                        />

                        <Textbox
                            name="data_nascimento"
                            label="Data de nascimento"
                            inputMode="numeric"
                            mask={masks.date}

                        />

                        <Select
                            name="sexo"
                            label="Sexo"
                            onChange={selected => setFieldValue('sexo', selected)}
                            options={[
                                { label: 'Masculino', value: 'M' },
                                { label: 'Feminino', value: 'F' }
                            ]}
                        />

                        <Textbox
                            name="email"
                            label="E-mail"
                            inputMode="email"
                        />

                        <Textbox
                            name="rg"
                            label="RG"
                            type="number"
                        />

                        <Textbox
                            name="rg_orgao"
                            label="Órgão expedidor"
                        />

                        <Textbox
                            name="rg_data_emissao"
                            label="Data de emissão do RG"
                            inputMode="numeric"
                            mask={masks.date}
                        />

                        <File
                            name="rg-arquivo"
                            onSuccess={fileId => setFieldValue('rg-arquivo', fileId)}
                            label="RG digitalizado"
                            format="square"
                            previewSize={['100%', '150px']}
                            error={errors['rg-arquivo']}
                            getPreloadImage={async () => {
                                if (values['rg-arquivo']) {
                                    const arquivo = await api.get(`arquivo/${values['rg-arquivo']}`)
                                    return arquivo?.link
                                }
                            }}
                            style={{ gridArea: 'rg_arq' }}
                        />

                        <File
                            name="cpf-arquivo"
                            onSuccess={fileId => setFieldValue('cpf-arquivo', fileId)}
                            label="CPF digitalizado"
                            format="square"
                            previewSize={['100%', '150px']}
                            error={errors['cpf-arquivo']}
                            getPreloadImage={async () => {
                                if (values['cpf-arquivo']) {
                                    const arquivo = await api.get(`arquivo/${values['cpf-arquivo']}`)
                                    return arquivo?.link
                                }
                            }}
                            style={{ gridArea: 'cpf_arq' }}
                        />

                        <File
                            name="foto"
                            onSuccess={fileId => setFieldValue('foto', fileId)}
                            label="Foto 3X4"
                            format="square"
                            previewSize={['100%', '150px']}
                            error={errors.foto}
                            getPreloadImage={async () => {
                                if (values.foto) {
                                    const arquivo = await api.get(`arquivo/${values.foto}`)
                                    return arquivo?.link
                                }
                            }}
                            style={{ gridArea: 'foto' }}
                            accept={['image/png', 'image/jpeg', 'image/bmp']}
                        />

                        <ActionFooter>
                            <Button type="submit" loading={isSubmitting} className="white">
                                Continuar
                                <FaArrowRight />
                            </Button>
                        </ActionFooter>
                    </Form>
                )}
            </Formik>
        </Container>
    )
}
